import { DateTime } from 'luxon';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { StockFundamentalsStockData } from '@benzinga/quotes-ui';
import type { BaseQuoteProfile } from '../../utils/quote/types';
import type { Nullable } from '@benzinga/utils';
import type { Financials } from '@benzinga/securities-manager';
import type { News } from '@benzinga/basic-news-manager';

export const formatProfileDataToStockFundamentalsData = ({
  profile,
  quote,
  fundamentals,
  fundamentalsAnnual,
}: {
  profile: BaseQuoteProfile;
  quote?: DelayedQuote;
  fundamentals?: Financials | null;
  fundamentalsAnnual?: Financials | null;
}): Nullable<StockFundamentalsStockData> => {
  const ratio = fundamentals?.valuationRatios?.[0];

  const stock = {
    ...(quote ?? profile?.richQuoteData),
    dividend: Array.isArray(fundamentalsAnnual?.earningReports)
      ? Number(fundamentalsAnnual?.earningReports[0]?.dividendPerShare)
      : null,
    dividendYield: ratio && ratio?.forwardDividendYield ? Number(ratio?.forwardDividendYield) * 100 : null,
    eps: Array.isArray(fundamentalsAnnual?.earningReports) ? fundamentalsAnnual?.earningReports[0]?.basicEps : null,
    forwardDividend: ratio && ratio?.forwardDividend ? Number(ratio?.forwardDividend) : null,
    payoutRatio: ratio ? ratio?.payoutRatio : null,
    pe: ratio ? ratio?.peRatio : null,
  } as Nullable<StockFundamentalsStockData>;

  return stock;
};

export interface GroupedNewsI {
  date: string;
  articles: News[];
}

export const splitQuoteNewsByDate = (news: News[], language = 'en'): GroupedNewsI[] => {
  if (!Array.isArray(news)) return [];

  const filteredNews = news
    .filter((v, i, a) => a.findIndex(t => t.id === v.id) === i)
    .sort((a, b) => {
      return new Date(b.created).getTime() - new Date(a.created).getTime();
    });

  const groups = filteredNews.reduce((articles, article) => {
    const parsedDate = DateTime.fromJSDate(new Date(article.created));
    const date = parsedDate.setLocale(language).toFormat('EEEE, MMMM dd, yyyy');

    if (!articles[date]) {
      articles[date] = [];
    }

    articles[date].push(article);

    return articles;
  }, {});

  const groupedNews: GroupedNewsI[] = Object.keys(groups).map(date => {
    return {
      articles: groups[date],
      date,
    };
  });

  return groupedNews;
};
